import { computed, makeObservable, observable, runInAction } from 'mobx';

import { deepMerge } from '@cutwise/utils';

import { DEFAULT_SNAPSHOT_ID } from '@/dmdrive/topic/constants/snapshot';
import { Snapshot, SnapshotModel } from '@/dmdrive/topic/entities/Snapshot';
import { TopicMarkModel } from '@/dmdrive/topic/entities/TopicMark';

import { TopicUser, TopicUserModel } from './TopicUser';

export enum TopicResourceType {
  CarbonViewer = 'cv',
  DMIndustrial = 'dmind',
  ImageViewer = 'img',
  LODViewer = 'lod',
}

interface TopicResource {
  meta: {
    title: string;
    model: string;
    targetModel: string;
    type: TopicResourceType;
    dataPath: string;
  };
  state: number;
}

export interface TopicMeta {
  title: string;
  fitness?: number;
  preview?: string;
}

export class TopicModel {
  created!: string;
  topicUsers!: TopicUserModel[];
  resource!: TopicResource;
  marks!: TopicMarkModel[];
  snapshots!: SnapshotModel[];
  urn!: string;
  id!: string;
  meta!: TopicMeta;
}

export class Topic extends TopicModel {
  topicUsers: TopicUser[] = [];
  snapshots: Snapshot[] = [];

  private topicOnlineUsers: TopicUserModel[] = [];

  constructor(model: TopicModel) {
    super();

    makeObservable(this, {
      topicUsers: observable,
      marks: observable,
      snapshots: observable,
      topicUsersSorted: computed,
      onlineUsers: computed,
    });

    this.merge(model);
  }

  get title() {
    return this.meta ? this.meta.title : this.resource.meta.title;
  }

  get topicUsersSorted() {
    const users = [...(this.topicUsers || [])];

    users.sort((a, b) => {
      if (a.isMe) {
        return -1;
      }

      if (b.isMe) {
        return 1;
      }

      return 0;
    });
    users.sort((a, b) => b.id - a.id);

    return users;
  }

  get onlineUsers() {
    return this.topicUsers?.filter((user) => user.isOnline);
  }

  get viewerType() {
    return this.resource?.meta?.type ?? null;
  }

  get owner() {
    return this.topicUsers ? this.topicUsers.find((member) => member.isOwner) : null;
  }

  get model() {
    return this.resource.meta.dataPath || this.resource.meta.model || '';
  }

  get targetModel() {
    return this.resource.meta.targetModel;
  }

  get isImage() {
    return this.viewerType === TopicResourceType.ImageViewer;
  }

  get isLODViewer() {
    return this.viewerType === TopicResourceType.LODViewer;
  }

  get isCarbonViewer() {
    return this.viewerType === TopicResourceType.CarbonViewer;
  }

  get isDMIndustrial() {
    return Boolean(this.resource.meta.model?.endsWith('.cbr') && this.resource.meta.targetModel?.endsWith('.cbr'));
  }

  get marksMetas() {
    return this.marks.map((mark) => mark.meta);
  }

  get areRemarksAvailable() {
    return this.isLODViewer || this.isDMIndustrial;
  }

  get fitness() {
    return this.meta?.fitness;
  }

  get preview() {
    return this.meta?.preview;
  }

  get defaultSnapshot() {
    return this.snapshots.find((snapshot) => snapshot.type === DEFAULT_SNAPSHOT_ID && snapshot.isMy);
  }

  setOnlineUsers(onlineUsers: TopicUserModel[]) {
    this.topicOnlineUsers = onlineUsers;

    this.applyTopicUsersStates();
  }

  merge(model: TopicModel) {
    runInAction(() => {
      (Object.keys(model) as (keyof TopicModel)[]).forEach((key) => {
        switch (key) {
          case 'topicUsers':
            this.topicUsers = model.topicUsers.map((user) => new TopicUser(user));

            break;
          case 'snapshots':
            this.snapshots = model.snapshots.map((snapshot) => new Snapshot(snapshot));

            break;
          default:
            this[key] = model[key] as never;

            break;
        }
      });
    });

    this.applyTopicUsersStates();
  }

  setMetaValue<T extends keyof TopicMeta>(key: T, value: TopicMeta[T]) {
    this.meta = {
      ...this.meta,
      [key]: value,
    };
  }

  private applyTopicUsersStates() {
    runInAction(() => {
      this.topicUsers = this.topicUsers.map((user) => {
        const onlineUser = this.topicOnlineUsers.find((onlineUser) => onlineUser.id === user.id);

        if (onlineUser) {
          return new TopicUser(deepMerge(user, onlineUser));
        }

        user.setOnline(false);

        return user;
      });
    });
  }
}
